import { useCallback, useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useMagicPenStore } from '@src/stores';

import SocketService from '@src/services/SocketService';

import {
  removeTtlTimeout,
  cancelAllTtlTimeouts,
  ttlTimeoutsArray,
  toRelativeCanvasObject,
} from '@src/components/MagicPen/helpers';

import {
  EMagicPenSockets,
  EMagicPenActionType,
  ICanvasObject,
  TAnnotationPayload,
  TDeleteActionPayload,
  TMagicPenPayload,
} from '@src/types';

// Shared helpers for adding Magic Pen objects locally and broadcasting them to peers.
const useMagicPenIntegration = (containerWidth?: number, containerHeight?: number) => {
  const { addObjects } = useMagicPenStore(
    useShallow((state) => ({
      addObjects: state.addObjects,
    }))
  );

  const emitMagicPenAction = useCallback(async (payload: TMagicPenPayload) => {
    try {
      await SocketService.request(EMagicPenSockets.MAGIC_PEN, payload);
    } catch (err) {
      console.error('Magic Pen error:', err);
    }
  }, []);

  const emitDeleteAnnotation = useCallback(
    async (objects: ICanvasObject[]) => {
      const validAnnotations = objects.filter((a) => a && 'id' in a);
      if (validAnnotations.length === 0) return;

      const annotationIds = validAnnotations.map((a) => a.id);

      const payload: TDeleteActionPayload = {
        actionType: EMagicPenActionType.DELETE_ANNOTATION,
        annotationIds,
      };

      await emitMagicPenAction(payload);
    },
    [emitMagicPenAction]
  );

  // Helper function to schedule deletion of objects with TTL (disappearing ink)
  const scheduleTtlDeletion = useCallback(
    (objects: ICanvasObject[]) => {
      const objectsWithTtl = objects.filter((obj) => obj.ttlMs && obj.ttlMs > 0);
      if (objectsWithTtl.length === 0) return;

      const maxTTL = Math.max(...objectsWithTtl.map((obj) => obj.ttlMs || 0)) || 0;

      const timeoutId = setTimeout(() => {
        removeTtlTimeout(timeoutId);

        emitDeleteAnnotation(objectsWithTtl);
      }, maxTTL);

      ttlTimeoutsArray.push(timeoutId);
    },
    [emitDeleteAnnotation]
  );

  // Cleanup: cancel all pending timeouts on unmount
  useEffect(() => {
    return () => {
      cancelAllTtlTimeouts();
    };
  }, []);

  // Broadcast an "add annotation" event so other peers receive newly created objects
  const emitAddAnnotation = useCallback(
    async (objects: ICanvasObject[]) => {
      // Schedule deletion for objects with ttlMs (disappearing ink)
      scheduleTtlDeletion(objects);

      //  Convert each object to relative representation and strip absolutes before sending (old: delete x, y, width, height)
      const relativeAnnotations = objects.map((object) => {
        const withRelative = toRelativeCanvasObject(object, containerWidth ?? 0, containerHeight ?? 0);
        const { x, y, width, height, ...rest } = withRelative;
        return rest;
      });

      const payload: TAnnotationPayload = {
        actionType: EMagicPenActionType.ADD_ANNOTATE,
        annotations: relativeAnnotations,
      };

      await emitMagicPenAction(payload);
    },
    [containerHeight, containerWidth, emitMagicPenAction, scheduleTtlDeletion]
  );

  // Broadcast an "update annotation" event so other peers stay in sync with edits
  const emitUpdateAnnotation = useCallback(
    async (objects: ICanvasObject[]) => {
      //  Convert each object to relative representation and strip absolutes before sending (old: delete x, y, width, height)
      const relativeAnnotations = objects.map((object) => {
        const withRelative = toRelativeCanvasObject(object, containerWidth ?? 0, containerHeight ?? 0);
        const { x, y, width, height, ...rest } = withRelative;
        return rest;
      });

      const payload: TAnnotationPayload = {
        actionType: EMagicPenActionType.UPDATE_ANNOTATE,
        annotations: relativeAnnotations,
      };

      await emitMagicPenAction(payload);
    },
    [containerHeight, containerWidth, emitMagicPenAction]
  );

  // Append a freshly created object locally, then emit it to other participants
  const handleAddObjects = useCallback(
    async (objects: ICanvasObject[]) => {
      // Add to store with both absolute and relative coordinates for local use
      // The store will assign z-index values to the objects
      const localObjects = objects.map((object) =>
        toRelativeCanvasObject(object, containerWidth ?? 0, containerHeight ?? 0)
      );

      // Add objects to store (this will assign z-index values) and get the updated objects back
      return new Promise<void>((resolve) => {
        addObjects(localObjects, (updatedObjects) => {
          // Emit socket event with relative coordinates and z-index values
          void emitAddAnnotation(updatedObjects).then(() => resolve());
        });
      });
    },
    [addObjects, emitAddAnnotation, containerWidth, containerHeight]
  );

  return {
    handleAddObjects,
    emitAddAnnotation,
    emitUpdateAnnotation,
    emitDeleteAnnotation,
  };
};

export default useMagicPenIntegration;
